import React from 'react'
import router from '@Site/Router'
import classnames from 'classnames'
import map from 'lodash/map'
import Projects from '@Images/sidebar_projects.svg'
import Assets from '@Images/sidebar_assets.svg'
import Vocabularies from '@Images/sidebar_vocabularies.svg'
import Guidelines from '@Images/sidebar_guidelines.svg'
import Termbases from '@Images/sidebar_termbases.svg'
import Channels from '@Images/sidebar_channels.svg'
import Labels from '@Images/sidebar_labels.svg'
import VoiceStudio from '@Images/sidebar_voice_studio.svg'
import LeftBorderActive from '@Images/sidebar_left_border_active.svg'

import './SidebarMenu.scss'

interface IPage {
  name: string
  displayedName: string
  paths: string[]
  icon: JSX.Element
}

const PAGES: IPage[] = [{
  name: 'projects',
  displayedName: 'Projects',
  paths: ['/'],
  icon: <Projects />
}, {
  name: 'assets',
  displayedName: 'Assets',
  paths: ['/assets'],
  icon: <Assets />
}, {
  name: 'vocabularies',
  displayedName: 'Vocabularies',
  paths: ['/customizations/vocabularies'],
  icon: <Vocabularies />
}, {
  name: 'guidelines',
  displayedName: 'Guidelines',
  paths: ['/customizations/guidelines'],
  icon: <Guidelines />
}, {
  name: 'termbases',
  displayedName: 'Term bases',
  paths: ['/customizations/termbases'],
  icon: <Termbases />
}, {
  name: 'voiceStudio',
  displayedName: 'Voice Studio',
  paths: ['/voice_studio'],
  icon: <VoiceStudio />
}, {
  name: 'channels',
  displayedName: 'Channels',
  paths: ['/channels/new','/channels/manage'],
  icon: <Channels />
}, {
  name: 'labels',
  displayedName: 'Labels',
  paths: ['/labels'],
  icon: <Labels />
}]

interface SidebarMenuProps {
  readonly pageName: string
  readonly hasExternalChannels: boolean
  readonly totalCountOfProjects: number
  readonly totalCountOfElements: number
  readonly totalCountOfSpeakerVoices: number
  readonly currentAccountHasCurrentSubscription: boolean
}

const SidebarMenu: React.FC<SidebarMenuProps> = props => {
  const {
    pageName,
    hasExternalChannels,
    totalCountOfProjects,
    totalCountOfElements,
    totalCountOfSpeakerVoices,
    currentAccountHasCurrentSubscription,
  } = props

  const activePageIndex = map(PAGES, page => page.name).indexOf(pageName)

  const renderLeftBorderActive = () => {
    return (
      <div className="SidebarMenu__item-left-border-active">
        <LeftBorderActive />
      </div>
    )
  }

  const renderProjectsActive = () => {
    return (
      <React.Fragment>
        <div className="SidebarMenu__item-number">
          {totalCountOfProjects}
        </div>
        {renderLeftBorderActive()}
      </React.Fragment>
    )
  }

  const renderAssetsActive = () => {
    return (
      <React.Fragment>
        <div className="SidebarMenu__item-number">
          {totalCountOfElements}
        </div>
        {renderLeftBorderActive()}
      </React.Fragment>
    )
  }

  const renderVoiceStudioActive = () => {
    return (
      <React.Fragment>
        <div className="SidebarMenu__item-number">
          {totalCountOfSpeakerVoices}
        </div>
        {renderLeftBorderActive()}
      </React.Fragment>
    )
  }

  const switchPage = (page: IPage) => {
    const pathIndex = page.name === 'channels' && hasExternalChannels ? 1 : 0

    router.navigate(page.paths[pathIndex])
  }

  const pageList = map(PAGES, (page, index) => {
    const activePage = activePageIndex > 0 ? PAGES[activePageIndex] : null
    const isChannelsPage = page.name === 'channels'
    const isProjectsPage = page.name === 'projects'
    const isAssetsPage = page.name === 'assets'
    const isVoiceStudioPage = page.name === 'voiceStudio'
    const projectsPageIsActive = activePage && activePage.name === 'projects'
    const assetsPageIsActive = activePage && activePage.name === 'assets'
    const voiceStudioPageIsActive = activePage && activePage.name === 'voiceStudio'
    const pageIsActive = index === activePageIndex
    const shouldSkip = isChannelsPage && !currentAccountHasCurrentSubscription

    const handleSwitchPage = () => {
      switchPage(page)
    }

    const renderListItem = () => {
      return (
        <li
          key={index}
          className={classnames(
            'SidebarMenu__item',
            { 'SidebarMenu__item-active': pageIsActive }
          )}
          onClick={handleSwitchPage}
        >
          {page.icon}
          {page.displayedName}
          {isProjectsPage && projectsPageIsActive && renderProjectsActive()}
          {isAssetsPage && assetsPageIsActive && renderAssetsActive()}
          {isVoiceStudioPage && voiceStudioPageIsActive && renderVoiceStudioActive()}
          {pageIsActive && !projectsPageIsActive && !assetsPageIsActive && !voiceStudioPageIsActive && renderLeftBorderActive()}
        </li>
      )
    }

    return (
      shouldSkip ? <React.Fragment /> : renderListItem()
    )
  })

  return (
    <div className="SidebarMenu">
      <ul className="SidebarMenu__items">
        {pageList}
      </ul>
    </div>
  )
}

export default SidebarMenu
